import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Layers, Smartphone } from 'lucide-react';
import { PRODUCTS } from '../constants';
import ProductCard from '../components/ProductCard';

const ProductsPage = () => {
  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section className="bg-slate-900 py-20 lg:py-28 relative overflow-hidden">
        {/* Abstract Background Shapes */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-brand-900/20 to-transparent pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-600/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center lg:text-left">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-6xl font-extrabold text-white tracking-tight mb-6 leading-tight">
              Powerful AI products <br className="hidden lg:block" />
              <span className="gradient-text text-transparent bg-clip-text bg-gradient-to-r from-brand-400 to-sky-200">
                built to ship — fast.
              </span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-300 mb-10 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              NyxSolutions builds production-grade AI applications and platforms for mobility,
              education, hospitality, sales teams, and enterprise workflows.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <Link
                to="/products"
                className="btn-primary w-full sm:w-auto px-8 py-3.5 bg-brand-600 text-white font-semibold rounded-lg hover:bg-brand-500 shadow-[0_0_20px_rgba(14,165,233,0.3)] flex items-center justify-center"
              >
                Explore Products
                <ArrowRight className="arrow-icon ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/about"
                className="btn-secondary w-full sm:w-auto px-8 py-3.5 bg-slate-800 text-slate-200 font-semibold rounded-lg border border-slate-700 hover:bg-slate-700 flex items-center justify-center"
              >
                Learn More
              </Link>
            </div>
            <p className="mt-6 text-sm text-slate-500 font-medium">
              Built for reliability, scaled for growth.
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Built for the real world</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              NyxSolutions designs and delivers end-to-end AI applications — from research and prototyping to scalable production deployments. We focus on clean UX, robust backends, and AI features that bring measurable value: routing & optimization, OCR-driven document intelligence, automated notifications, offline-first mobile experiences, and conversational agents.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="p-6 bg-slate-50 rounded-xl">
              <div className="w-12 h-12 bg-blue-100 text-brand-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Zap size={24} />
              </div>
              <h3 className="font-semibold text-gray-900">High Performance</h3>
              <p className="text-sm text-gray-600 mt-2">Optimized for speed and scale using SSE and robust backends.</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-xl">
               <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Smartphone size={24} />
              </div>
              <h3 className="font-semibold text-gray-900">Mobile First</h3>
              <p className="text-sm text-gray-600 mt-2">Offline-capable mobile apps designed for field operations.</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-xl">
               <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Layers size={24} />
              </div>
              <h3 className="font-semibold text-gray-900">AI Integration</h3>
              <p className="text-sm text-gray-600 mt-2">Native AI features like OCR, routing, and predictions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-20 bg-gray-50" id="featured-products">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Featured Products</h2>
              <p className="mt-3 text-gray-600 max-w-2xl">
                Explore our suite of specialized applications designed for specific industry needs.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {PRODUCTS.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

        </div>
      </section>

    </div>
  );
};

export default ProductsPage;

